import React, { useCallback, useEffect, useState } from "react";
import { Box, Divider, Paper, Typography } from "@mui/material";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import { FraudFlag, getFraudFlag } from "../../services/fraudService";
import { formatDateTime, fraudReviewerName, fraudSignalLabel, partySplit, responsibleLabel } from "../../utils/fraud";
import { FraudPctChip, FraudStatusChip } from "./FraudChips";
import FraudExplanation from "./FraudExplanation";
import FraudReviewActions from "./FraudReviewActions";

/** The ride page's "Fraud review" panel; renders nothing when the ride is not flagged (or the lookup fails). */
const RideFraudPanel: React.FC<{ rideId: number; canReview: boolean }> = ({ rideId, canReview }) => {
    const [flag, setFlag] = useState<FraudFlag | null>(null);

    const load = useCallback(async () => {
        try {
            const res = await getFraudFlag(rideId);
            setFlag(res.data.flag);
        } catch {
            setFlag(null);
        }
    }, [rideId]);

    useEffect(() => {
        load();
    }, [load]);

    if (!flag) return null;

    return (
        <Paper data-testid="ride-fraud-panel" sx={{ p: 3, mb: 3, borderRadius: 2, borderLeft: "4px solid #d32f2f" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                <GppMaybeIcon color="error" />
                <Typography variant="h6">Fraud review</Typography>
                <FraudPctChip fraudPct={flag.fraudPct} certainty={flag.explanation.certainty} />
                <FraudStatusChip status={flag.status} />
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" sx={{ mb: 2, fontWeight: 600 }} data-testid="fraud-party-split">
                Responsibility: {partySplit(flag)}
            </Typography>
            <Box sx={{ mb: 2 }}>
                <FraudExplanation explanation={flag.explanation} />
            </Box>
            <Typography variant="subtitle2">Warning signs</Typography>
            <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1 }}>
                {flag.signals.map((s, i) => (
                    <li key={`${s.code}-${i}`}>
                        <Typography variant="body2">
                            <strong>{fraudSignalLabel(s.code)}</strong> — {s.detail}
                        </Typography>
                    </li>
                ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Flagged by the nightly review on {formatDateTime(flag.createdAt)} · score {flag.score}. A flag never changes billing.
            </Typography>
            {flag.status !== "OPEN" && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Reviewed by {fraudReviewerName(flag.reviewedBy)} on {formatDateTime(flag.reviewedAt)}
                </Typography>
            )}
            {flag.responsibleParties.length > 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Responsible: {responsibleLabel(flag.responsibleParties)}
                </Typography>
            )}
            {flag.note && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                    Note: {flag.note}
                </Typography>
            )}
            {canReview && <FraudReviewActions rideId={rideId} flag={flag} onChanged={load} />}
        </Paper>
    );
};

export default RideFraudPanel;
